// ============ src/widgets/FullScreenQR.jsx
import { useEffect, useState, useMemo, useRef } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import {
  Facebook, Twitter, Copy, Share2, Check, ExternalLink,
  Loader2, ArrowRight, ArrowLeft, QrCode, Link2, Globe,
  CalendarDays, Clock, Trophy, Monitor, Smartphone, ShieldCheck,
  LayoutGrid
} from 'lucide-react'
import { QRCodeCanvas } from 'qrcode.react'
import { getTestRunDetail } from '@/api/kuizy' // ใช้ตัวที่รับ params object
import { formatDateTime } from '@/settings/master'
import { motion } from 'framer-motion'

const b64d = s => { try { return atob(s || '') } catch { return '' } }

const humanAttempt = (rule, limit) =>
  rule === 'unlimited' ? 'ไม่จำกัด'
    : rule === 'untilPass' ? 'จนกว่าจะผ่าน'
      : rule === 'limited' ? `จำกัด ${limit ?? 1} ครั้ง` : '-'

const humanSolution = v =>
  v === 'full' ? 'แสดงคำถาม+เฉลย'
    : v === 'question' ? 'เฉพาะคำถาม'
      : v === 'none' ? 'ไม่แสดง' : '-'

export default function FullScreenQR() {
  const { encodedorgId, encodedTestRunId } = useParams()
  const orgId = b64d(encodedorgId)
  const testRunId = b64d(encodedTestRunId)
  const nav = useNavigate()

  // ลิงก์เข้าทำสอบ (แก้ให้ไป /play/:orgId/:runId)
  const examLinkTest = useMemo(
    () => `${window.location.origin}/play/${orgId}/${testRunId}/exam?preview=1`,
    [orgId, testRunId]
  )

    const examLink = useMemo(
    () => `${window.location.origin}/play/${orgId}/${testRunId}`,
    [orgId, testRunId]
  )

  const [detail, setDetail] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [count, setCount] = useState('')
  const [copied, setCopied] = useState(false)

  const qrRef = useRef(null)

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await getTestRunDetail({ org_id: orgId, run_id: testRunId })
        if (!data) throw new Error('not_found')
        setDetail(data)
      } catch {
        setError('ไม่พบข้อมูลการสอบนี้')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [orgId, testRunId])

  useEffect(() => {
    if (!detail) return
    const span = ms => {
      let s = Math.max(0, Math.floor(ms / 1000))
      const d = Math.floor(s / 86400); s -= d * 86400
      const h = Math.floor(s / 3600); s -= h * 3600
      const m = Math.floor(s / 60); s -= m * 60
      return `${d} วัน ${h} ชม. ${m} นาที ${s} วิ`
    }
    const tick = () => {
      const now = Date.now()
      const st = detail.test.start_time ? new Date(detail.test.start_time).getTime() : null
      const ed = detail.test.end_time ? new Date(detail.test.end_time).getTime() : null
      if (st && now < st) setCount(`เริ่มสอบใน ${span(st - now)}`)
      else if (ed && now <= ed) setCount(`เปิดสอบ • เหลือ ${span(ed - now)}`)
      else if (st || ed) setCount('หมดเวลาสอบแล้ว')
      else setCount('พร้อมใช้งาน')
    }
    tick()
    const t = setInterval(tick, 1000)
    return () => clearInterval(t)
  }, [detail])

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(examLink)
      setCopied(true); setTimeout(() => setCopied(false), 1200)
    } catch { }
  }

  const downloadQR = () => {
    const canvas = qrRef.current?.querySelector('canvas')
    if (!canvas) return
    const url = canvas.toDataURL('image/png')
    const a = document.createElement('a')
    a.href = url
    a.download = `kuizy-qr-${testRunId}.png`
    a.click()
  }

 

  const share = url => window.open(url, '_blank', 'noopener,width=620,height=720')
  const shareFB = () => share(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(examLink)}`)
  const shareTw = () => share(`https://twitter.com/intent/tweet?url=${encodeURIComponent(examLink)}`)
  const shareLine = () => share(`https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(examLink)}`)

  const meta = useMemo(() => {
    if (!detail) return []
    const { test, run, settings } = detail
    return [
      { icon: Globe, label: 'ประเภท', value: run.scope === 'public' ? 'สาธารณะ' : 'เฉพาะห้องเรียน' },
      { icon: LayoutGrid, label: 'จำนวนข้อสอบ', value: test.question_count },
      { icon: Trophy, label: 'รอบที่', value: run.run_no },
      { icon: ShieldCheck, label: 'ทำซ้ำได้', value: humanAttempt(settings.attemptRule, settings.attemptLimit) },
      { icon: Clock, label: 'เวลาทำ', value: `${test.duration} นาที` },
      { icon: Trophy, label: 'เกณฑ์ผ่าน', value: `${test.criteria}%` },
      { icon: CalendarDays, label: 'เริ่ม', value: formatDateTime(test.start_time) },
      { icon: CalendarDays, label: 'สิ้นสุด', value: formatDateTime(test.end_time) },
      {
        icon: Monitor,
        label: 'แพลตฟอร์ม',
        value: settings.platform === 'mobileOnly'
          ? 'Mobile'
          : settings.platform === 'webOnly'
            ? 'Web'
            : 'Web + Mobile'
      }
    ]
  }, [detail])

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-gradient-to-br from-sky-50 to-white">
        <div className="flex items-center gap-3 text-gray-700">
          <Loader2 className="animate-spin" /> กำลังโหลด…
        </div>
      </div>
    )
  }

  if (error || !detail) {
    return (
      <div className="min-h-screen grid place-items-center bg-gradient-to-br from-rose-50 to-white">
        <div className="bg-white rounded-2xl shadow-lg border border-rose-200 px-6 py-5 text-rose-600">
          {error || 'ไม่พบข้อมูล'}
        </div>
      </div>
    )
  }

  const { test } = detail

  return (
    <motion.section
      initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: .35 }}
      className="min-h-screen relative overflow-hidden"
    >
      {/* พื้นหลังสวยๆ */}
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(1200px_600px_at_-10%_-10%,#c7e7ff_0%,transparent_60%),radial-gradient(1000px_500px_at_110%_110%,#fde1ff_0%,transparent_55%)]" />
      <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-sky-200/40 blur-3xl" />
      <div className="absolute -bottom-24 -left-24 w-[380px] h-[380px] rounded-full bg-fuchsia-200/40 blur-3xl" />

      <div className="min-h-screen py-10 px-4 flex items-center justify-center">
        <div className="w-full max-w-5xl grid grid-cols-1 lg:grid-cols-5 gap-5">
          {/* Hero / Info */}
          <div className="lg:col-span-2">
            <div className="h-full rounded-3xl bg-white/70 backdrop-blur border border-white/40 shadow-xl p-6">
              <div className="flex items-center gap-3">
                <div className="size-11 rounded-2xl bg-primary/10 grid place-items-center">
                  <QrCode className="size-6 text-primary" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold leading-snug">{test.name}</h2>
                  <p className="text-sm text-gray-500">
                    {test.content_name
                      ? `${test.content_name}${test.content_code ? ` (${test.content_code})` : ''}`
                      : (test.content_code || '')}
                  </p>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-3">
                {meta.map(({ icon: Icon, label, value }) => (
                  <div key={label} className="rounded-xl border border-gray-200 bg-white p-3">
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      <Icon className="size-4 text-primary" />
                      <span>{label}</span>
                    </div>
                    <div className="mt-1 text-sm font-medium text-gray-900">{value}</div>
                  </div>
                ))}
              </div>

              <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                <div className="flex items-center gap-2">
                  <Clock className="size-4" />
                  <span className="font-medium">
                    {count === 'พร้อมใช้งาน' ? 'สถานะ: พร้อมใช้งาน' :
                      count === 'หมดเวลาสอบแล้ว' ? 'สถานะ: หมดเวลาสอบแล้ว' : count}
                  </span>
                </div>
              </div>

              <div className="mt-6 flex gap-2">

                <a
                  href={examLinkTest} target="_blank" rel="noopener noreferrer"
                  className="h-10 px-4 rounded-xl bg-primary text-white flex items-center gap-2 hover:opacity-95"
                >
                  ทดสอบทำข้อสอบ <ExternalLink className="size-4" />
                </a>
              </div>
            </div>
          </div>

          {/* QR Card */}
          <div className="lg:col-span-3">
            <div className="rounded-3xl bg-white/80 backdrop-blur border border-white/40 shadow-2xl p-6">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600 flex items-center gap-2">
                  <Link2 className="size-4 text-primary" />
                  ลิงก์เข้าทำข้อสอบ
                </div>
                <div className="flex gap-2">
                  {/* <button
                    onClick={webShare}
                    className="h-9 px-3 rounded-lg border border-gray-300 text-gray-700 flex items-center gap-2 hover:bg-gray-50"
                  >
                    <Share2 className="size-4" /> แชร์
                  </button> */}
                  <button
                    onClick={copyLink}
                    className="h-9 px-3 rounded-lg border border-gray-300 text-gray-700 flex items-center gap-2 hover:bg-gray-50"
                  >
                    {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
                    {copied ? 'คัดลอกแล้ว' : 'คัดลอก'}
                  </button>
                </div>
              </div>

              <a
                href={examLink} target="_blank" rel="noopener noreferrer"
                className="block mt-2 text-sm text-primary break-all hover:underline"
              >
                {examLink}
              </a>

              <div className="mt-6 flex flex-col items-center">
                <div ref={qrRef} className="bg-white p-4 rounded-2xl shadow-lg border border-gray-200">
                  <QRCodeCanvas value={examLink} size={330} />
                </div>

                <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                  <button
                    onClick={downloadQR}
                    className="h-9 px-3 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    ดาวน์โหลด QR
                  </button>
                  <button onClick={shareFB} className="h-9 px-3 rounded-lg bg-[#3b5998] text-white hover:opacity-90">
                    แชร์ Facebook
                  </button>
                  <button onClick={shareTw} className="h-9 px-3 rounded-lg bg-[#1da1f2] text-white hover:opacity-90">
                    แชร์ Twitter
                  </button>
                  <button onClick={shareLine} className="h-9 px-3 rounded-lg bg-[#06c755] text-white hover:opacity-90">
                    แชร์ LINE
                  </button>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs text-gray-600">
                <div className="flex items-center gap-2">
                  <Monitor className="size-4" /> รองรับ: Web
                </div>
                <div className="flex items-center gap-2">
                  <Smartphone className="size-4" /> รองรับ: Mobile
                </div>
                <div className="flex items-center gap-2">
                  <ShieldCheck className="size-4" /> ลิงก์ปลอดภัย (HTTPS)
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                {/* <a
                  href={examLink} target="_blank" rel="noopener noreferrer"
                  className="h-10 px-5 rounded-xl bg-secondary text-white flex items-center gap-2 hover:opacity-95"
                >
                  ไปยังหน้าสอบ <ArrowRight className="size-4" />
                </a> */}
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.section>
  )
}
